import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes } from '@/auth/msalConfig';
import { devMode, devUser } from '@/auth/devMode';
import type {
  SubscriptionSummary,
  SubscriptionListParams,
  SubscriptionListResponse,
  SubscriptionDetail,
  CreateSubscriptionRequest,
  CreateSubscriptionResult,
  UpdateSubscriptionRequest,
  ReportCatalogItem,
  ReportParameterDefinition,
  ResolveParameterValuesRequest,
  ResolvedParameterValues,
  RunHistoryEntry,
  RunHistoryPage,
  AuditEntry,
  Me,
  ScheduleDefinition,
  SchedulePreview,
  OutboxEmail,
  KtInsightsDeliveriesResponse,
  KtInsightsExportHistoryResponse,
  KtInsightsReportsResponse,
  KtInsightsReportPagesResponse,
  KtInsightsReportFiltersResponse,
  KtInsightsSubscription,
  KtInsightsHeadOffice,
  KtInsightsRecipient,
  KtInsightsExportStatus,
  KtInsightsRunNowResult,
  CreateKtInsightsSubscriptionRequest,
  UpdateKtInsightsSubscriptionRequest,
  HiddenCatalogRule,
  CreateHiddenCatalogRuleRequest,
  VisibilityDiagnostics,
  DirectoryUser,
  BulkReassignOwnerResult,
  SubscriptionRecipients,
  ReportParameterValue,
  RecipientCheckResult,
  PickupSearchResponse,
  PickupFileSummary,
  PickupDeliveryDto,
  PickupDownloadRecord,
  PickupStatusResponse,
  EnablePickupRequest,
  CustomViewCatalog,
  CustomViewDraft,
  CustomViewValidationResult,
  CustomViewApplyResult,
  HeadOfficeSearchResponse,
  ReportPackSummary,
  ReportPackDetail,
  ReportPackEligibility,
  SaveReportPackRequest,
  ReportPackEnrolmentDto,
  PreviewReportPackRequest,
  ReportPackApplyPreview,
  ApplyReportPackRequest,
  ReportPackApplyResult,
  ReportPackUnenrolResult
} from '@/types';

const apiBase = import.meta.env.VITE_API_BASE ?? '/api';

/**
 * The query string for a subscription list request.
 *
 * Empty strings are dropped as well as undefined, so clearing the search box asks for the unfiltered
 * list rather than for rows matching "" — and, just as importantly, produces the same URL the page
 * first loaded with, which is what lets react-query serve it from cache instead of re-fetching.
 */
function subscriptionListQuery(params: SubscriptionListParams): string {
  const qs = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '' && v !== false)
      .map(([k, v]) => [k, String(v)])
  ).toString();
  return qs ? `?${qs}` : '';
}

/**
 * Entra will not issue a token until the user does something about it — the
 * session has expired, conditional access wants a prompt, consent was revoked.
 * Its own type so callers can tell "sign in again" apart from an API that
 * answered with an error, and so react-query knows not to retry it.
 */
export class SignInRequiredError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'SignInRequiredError';
  }
}

/**
 * An error the API answered with, keeping the parts a caller may need to act on rather than only
 * display. The recipient check needs all three: the code to recognise its own refusal, and `data`
 * to render which recipients were objected to and to echo the confirmation fingerprint back.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    readonly data: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Whether a rejection is a request that was called off, rather than one that failed.
 *
 * The distinction is the whole point of threading an AbortSignal in: a superseded lookup that
 * never came back has told us nothing about the report server, and rendering it as a failure puts
 * "the report could not be asked" on screen for a question nobody is waiting on the answer to any
 * more. `fetch` rejects with a DOMException named AbortError; react-query's own CancelledError has
 * no such name and carries `silent`/`revert` instead, and both are matched here so the caller does
 * not have to know which layer called the request off first.
 */
export function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; silent?: boolean; revert?: boolean };
  return e.name === 'AbortError' || e.silent === true || e.revert === true;
}

export function useApi() {
  // Dev mode deliberately mounts no MsalProvider. useMsal is only useContext, so
  // outside a provider it yields msal-react's stub context instead of throwing,
  // and nothing below reads it — every path that touches `msal` sits behind a
  // devMode check. Calling it unconditionally is what keeps hook order stable.
  const msal = useMsal();

  async function getToken(): Promise<string | null> {
    if (devMode) return null;
    const account = msal.accounts[0] ?? msal.instance.getActiveAccount();
    if (!account) throw new SignInRequiredError('You are not signed in.');
    try {
      const result = await msal.instance.acquireTokenSilent({ scopes: apiScopes, account });
      return result.accessToken;
    } catch (e) {
      // Nothing interactive belongs here. getToken only ever runs from a
      // react-query fetch, never from a click, so acquireTokenPopup's
      // window.open lands outside a user gesture: a managed browser blocks it,
      // MSAL's promise never settles, and the caller waits forever with no
      // error to render. acquireTokenRedirect does settle, but it throws away
      // whatever is on screen — a half-filled form, discarded by a background
      // refetch — and the auto sign-in loop guard cannot cover it, because that
      // guard clears itself whenever an account is cached, which stays true
      // when it is only the API scope Entra is refusing. So report it and let
      // the user start the redirect from a button, the same way the app already
      // treats a 401.
      if (e instanceof InteractionRequiredAuthError) {
        throw new SignInRequiredError('Your sign-in has expired. Sign in again to continue.', { cause: e });
      }
      throw e;
    }
  }

  /**
   * `signal` is optional and only the callers that can be superseded pass one. An abandoned request
   * is not free: it holds a connection, and — for the parameter-value lookups — a slot on a report
   * server that answers these one at a time, so a cascade the user has already clicked past keeps
   * the answer they are waiting for queued behind it. See resolveParameterValues.
   */
  async function send<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (devMode) {
      headers['X-Dev-User'] = devUser.objectId;
    } else {
      const token = await getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(`${apiBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal
    });
    if (!res.ok) {
      let detail = res.statusText;
      let code: string | null = null;
      let data: unknown = null;
      try {
        const problem = await res.json();
        detail = problem.detail ?? problem.title ?? detail;
        code = problem.type ?? null;
        data = problem.data ?? null;
      } catch { /* not JSON */ }
      throw new ApiError(`API ${res.status}: ${detail}`, res.status, code, data);
    }
    if (res.status === 204) return undefined as unknown as T;
    // Not only 204. Run now answers 202 Accepted with no body at all, and `res.json()` on an empty
    // body rejects with a SyntaxError — which arrives at the caller looking exactly like a failed
    // run, so a run that was accepted reported itself as broken. It went unnoticed because the one
    // caller (the detail page's Run now) renders no error: the button simply came back and the
    // page never refreshed. Reading the text first is what tells "no content" apart from "content
    // that isn't JSON", which is still worth throwing over.
    const text = await res.text();
    if (!text) return undefined as unknown as T;
    return JSON.parse(text) as T;
  }

  /**
   * Fetches a file as a Blob rather than JSON.
   *
   * A pickup download cannot be a plain `<a href>` the way the dev outbox link is: the endpoint
   * needs the bearer token and an anchor sends no Authorization header. Going through fetch is
   * also what lets a 410 (retention removed the file) surface as a readable message rather than
   * the browser navigating away to an error document.
   */
  async function download(path: string): Promise<{ blob: Blob; fileName: string }> {
    const headers: Record<string, string> = {};
    if (devMode) {
      headers['X-Dev-User'] = devUser.objectId;
    } else {
      const token = await getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(`${apiBase}${path}`, { method: 'GET', headers });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const problem = await res.json();
        detail = problem.detail ?? problem.title ?? detail;
      } catch { /* not JSON */ }
      throw new Error(`API ${res.status}: ${detail}`);
    }

    // Content-Disposition carries the server's name for the file; fall back to a generic one so
    // a proxy that strips the header still produces a usable download.
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const fileName = match ? decodeURIComponent(match[1]) : 'report';

    return { blob: await res.blob(), fileName };
  }

  /**
   * One page of the execution log plus the pre-paging total.
   *
   * A local function rather than only a member, because the two run-history methods below are the
   * same request read two ways and both need it — an object literal cannot refer to its own
   * siblings.
   */
  function runHistoryPage(params: Record<string, string | number | undefined>) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    ).toString();
    return send<RunHistoryPage>('GET', `/run-history${qs ? `?${qs}` : ''}`);
  }

  return {
    me: () => send<Me>('GET', '/me'),

    /**
     * One page of the caller's subscriptions, already filtered. Returns an envelope rather than an
     * array because a page on its own cannot say how much there is, and "1–50 of 282" is the point.
     */
    listMine: (params: SubscriptionListParams = {}) =>
      send<SubscriptionListResponse>('GET', `/subscriptions/mine${subscriptionListQuery(params)}`),
    getSubscription: (id: string) => send<SubscriptionDetail>('GET', `/subscriptions/${id}`),
    /**
     * Creates the subscription, and says whether the pickup route it asked for happened — those
     * are two answers, not one. See {@link CreateSubscriptionResult}.
     */
    createSubscription: (req: CreateSubscriptionRequest) =>
      send<CreateSubscriptionResult>('POST', '/subscriptions', req),
    updateSubscription: (id: string, req: UpdateSubscriptionRequest) => send<void>('PUT', `/subscriptions/${id}`, req),
    deleteSubscription: (id: string) => send<void>('DELETE', `/subscriptions/${id}`),
    runNow: (id: string) => send<void>('POST', `/subscriptions/${id}/run-now`),

    /**
     * Whether these recipients belong with these parameters, without saving.
     *
     * Only powers the inline warning while someone is editing. The same check runs inside the
     * create and update endpoints, so it cannot be skipped by not calling this.
     */
    checkRecipients: (recipients: SubscriptionRecipients, parameters: ReportParameterValue[]) =>
      send<RecipientCheckResult>('POST', '/subscriptions/check-recipients', { recipients, parameters }),
    enable: (id: string) => send<void>('POST', `/subscriptions/${id}/enable`),
    disable: (id: string) => send<void>('POST', `/subscriptions/${id}/disable`),

    previewSchedule: (schedule: ScheduleDefinition) =>
      send<SchedulePreview>('POST', '/schedules/preview', schedule),

    ktInsightsDeliveries: () => send<KtInsightsDeliveriesResponse>('GET', '/ktinsights/deliveries'),
    ktInsightsReports: () => send<KtInsightsReportsResponse>('GET', '/ktinsights/reports'),
    ktInsightsReportPages: (reportId: number) =>
      send<KtInsightsReportPagesResponse>('GET', `/ktinsights/reports/${reportId}/pages`),
    ktInsightsReportFilters: (reportId: number) =>
      send<KtInsightsReportFiltersResponse>('GET', `/ktinsights/reports/${reportId}/filters`),
    ktInsightsHeadOffices: (q: string) =>
      send<KtInsightsHeadOffice[]>('GET', `/ktinsights/head-offices?q=${encodeURIComponent(q)}`),
    /**
     * `pages` is deliberately optional and the picker omits it. Passing the ticked pages would put
     * this MANDW01 round trip behind every checkbox; without them the answer is the recipient's
     * whole entitlement for the report, which the form intersects locally.
     */
    ktInsightsRecipientCheck: (reportId: number, email: string, pages?: string) =>
      send<KtInsightsRecipient>('GET',
        `/ktinsights/recipient-check?reportId=${reportId}&email=${encodeURIComponent(email)}`
        + (pages ? `&pages=${encodeURIComponent(pages)}` : '')),
    ktInsightsExportHistory: (params: Record<string, string | number | undefined>) => {
      const qs = new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)])
      ).toString();
      return send<KtInsightsExportHistoryResponse>('GET', `/ktinsights/export-history${qs ? `?${qs}` : ''}`);
    },
    ktInsightsSubscriptions: () => send<KtInsightsSubscription[]>('GET', '/ktinsights/subscriptions'),
    createKtInsightsSubscription: (req: CreateKtInsightsSubscriptionRequest) =>
      send<{ id: string }>('POST', '/ktinsights/subscriptions', req),
    updateKtInsightsSubscription: (id: string, req: UpdateKtInsightsSubscriptionRequest) =>
      send<void>('PUT', `/ktinsights/subscriptions/${id}`, req),
    deleteKtInsightsSubscription: (id: string) => send<void>('DELETE', `/ktinsights/subscriptions/${id}`),
    enableKtInsightsSubscription: (id: string, enabled: boolean) =>
      send<void>('POST', `/ktinsights/subscriptions/${id}/${enabled ? 'enable' : 'disable'}`),
    runKtInsightsSubscription: (id: string) =>
      send<KtInsightsRunNowResult>('POST', `/ktinsights/subscriptions/${id}/run-now`),
    ktInsightsExportStatus: (id: string) =>
      send<KtInsightsExportStatus | undefined>('GET', `/ktinsights/subscriptions/${id}/export-status`),

    listOutbox: (take = 100) => send<OutboxEmail[]>('GET', `/dev/outbox?take=${take}`),
    outboxContentUrl: (id: string) => `${apiBase}/dev/outbox/${encodeURIComponent(id)}/content`,
    tickScheduler: () => send<{ fired: number }>('POST', '/dev/scheduler/tick'),

    searchCatalog: (q?: string) =>
      send<ReportCatalogItem[]>('GET', `/reports/catalog${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    reportParameters: (path: string) =>
      send<ReportParameterDefinition[]>('GET', `/reports/parameters?path=${encodeURIComponent(path)}`),
    /**
     * The allowed values for a report's parameters, resolved against the choices already made.
     *
     * Kept apart from reportParameters on purpose: that one reads the catalog in milliseconds,
     * while this makes the report server run the report's own parameter datasets and was measured
     * at 1.2-2.0s against MANDW01. It is only ever called after the form has already rendered.
     *
     * The only method here that takes a signal, because it is the only one worth cancelling.
     * MANDW01 answers these one at a time: five overlapping lookups measured in production took
     * 15.9s / 15.6s / 9.0s / 7.6s / 7.0s and all finished within 200ms of each other, while the
     * same call made alone takes 1.0-1.9s — so the cost of a lookup nobody is waiting for is paid
     * by the one they ARE waiting for. Optional so no other caller has to change; see
     * useResolvedValues for the one that passes it.
     */
    resolveParameterValues: (req: ResolveParameterValuesRequest, signal?: AbortSignal) =>
      send<ResolvedParameterValues[]>('POST', '/reports/parameter-values', req, signal),

    /** The page and its total, for the run history screen — the only caller that pages. */
    runHistoryPage,

    /**
     * The rows only, for the subscription detail page's run-history section: it shows a fixed 15
     * most-recent runs of one report and links out to the full log for anything older, so a total
     * it would never render is noise. Kept as its own method rather than making that page write
     * `.entries` — the count is meaningful to exactly one screen.
     */
    runHistory: (params: Record<string, string | number | undefined>) =>
      runHistoryPage(params).then(page => page.entries),

    /** One page of the tenant-wide list, plus the by-owner roll-up the screen groups by. */
    listAllSubs: (params: SubscriptionListParams = {}) =>
      send<SubscriptionListResponse>('GET', `/admin/subscriptions${subscriptionListQuery(params)}`),
    listStale: (days = 7) => send<SubscriptionSummary[]>('GET', `/admin/stale?days=${days}`),
    listFailures: (sinceMinutes = 1440) =>
      send<RunHistoryEntry[]>('GET', `/admin/failures?sinceMinutes=${sinceMinutes}`),
    listUnowned: () => send<SubscriptionSummary[]>('GET', '/admin/unowned'),
    /**
     * Reassigns the PORTAL's own ownership record only — never MANDW01's `dbo.Subscriptions.OwnerID`,
     * which is the Windows account the report server actually sends mail as. The portal has no write
     * path to that at all. `newOwnerDisplayName` is carried along purely so the audit trail and the
     * UI can show a name without a second directory round trip; it plays no part in the reassignment
     * itself.
     */
    reassignOwner: (id: string, newOwnerObjectId: string, newOwnerDisplayName?: string | null) =>
      send<void>('POST', `/admin/subscriptions/${id}/owner`, { newOwnerObjectId, newOwnerDisplayName }),
    /** Same reassignment, applied to several subscriptions in one request. Partial failure is reported, not thrown. */
    bulkReassignOwner: (subscriptionIds: string[], newOwnerObjectId: string, newOwnerDisplayName?: string | null) =>
      send<BulkReassignOwnerResult[]>('POST', '/admin/subscriptions/reassign-owner-bulk',
        { subscriptionIds, newOwnerObjectId, newOwnerDisplayName }),
    /** Entra directory search for the owner-reassignment picker. */
    searchUsers: (q: string) =>
      send<DirectoryUser[]>('GET', `/admin/users/search?q=${encodeURIComponent(q)}`),

    listHiddenReports: () => send<HiddenCatalogRule[]>('GET', '/admin/hidden-reports'),
    hideReport: (req: CreateHiddenCatalogRuleRequest) =>
      send<HiddenCatalogRule>('POST', '/admin/hidden-reports', req),
    unhideReport: (id: number) => send<void>('DELETE', `/admin/hidden-reports/${id}`),
    visibilityDiagnostics: () => send<VisibilityDiagnostics>('GET', '/admin/visibility'),

    queryAudit: (params: Record<string, string | number | undefined>) => {
      const qs = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      ).toString();
      return send<AuditEntry[]>('GET', `/audit${qs ? `?${qs}` : ''}`);
    },

    // ── Report pickup ──────────────────────────────────────────────────────────────────
    pickupStatus: () => send<PickupStatusResponse>('GET', '/pickup/status'),

    pickupSearch: (params: Record<string, string | number | boolean | undefined>) => {
      const qs = new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)])
      ).toString();
      return send<PickupSearchResponse>('GET', `/pickup/files${qs ? `?${qs}` : ''}`);
    },

    pickupFile: (id: string) => send<PickupFileSummary>('GET', `/pickup/files/${id}`),

    /**
     * Downloads a file. Every call is recorded in the audit log before any bytes are sent —
     * the API commits the record first and refuses the download if that write fails, so there
     * is no unaudited path to this data.
     */
    pickupDownload: (id: string) => download(`/pickup/files/${id}/download`),

    pickupDeliveries: () => send<PickupDeliveryDto[]>('GET', '/pickup/deliveries'),

    /**
     * Head offices matching a typeahead query, for the pickup "Client / head office" field.
     * Backed by the Profiles API, not the KT Insights head-office lookup — see
     * `PickupHeadOfficePicker` for why the two must not be conflated.
     *
     * `available: false` means the lookup itself could not be attempted (no subscription key, or
     * the call failed) — never returned as a plain empty array precisely so the picker cannot
     * mistake "could not check" for "no such head office".
     */
    pickupHeadOfficeSearch: (q: string) =>
      send<HeadOfficeSearchResponse>('GET', `/pickup/head-offices?q=${encodeURIComponent(q)}`),

    pickupEnable: (req: EnablePickupRequest) => send<PickupDeliveryDto>('POST', '/pickup/deliveries', req),

    pickupDisable: (subscriptionId: string) => send<void>('DELETE', `/pickup/deliveries/${subscriptionId}`),

    pickupDownloadLog: (params: Record<string, string | number | undefined>) => {
      const qs = new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)])
      ).toString();
      return send<PickupDownloadRecord[]>('GET', `/pickup/downloads${qs ? `?${qs}` : ''}`);
    },

    // ── Report packs (admin only, behind the reportPacks feature flag) ──────────────────
    listPacks: () => send<ReportPackSummary[]>('GET', '/admin/packs'),
    getPack: (packId: string) => send<ReportPackDetail>('GET', `/admin/packs/${packId}`),
    createPack: (req: SaveReportPackRequest) => send<{ id: string }>('POST', '/admin/packs', req),
    updatePack: (packId: string, req: SaveReportPackRequest) =>
      send<void>('PUT', `/admin/packs/${packId}`, req),
    deletePack: (packId: string) => send<void>('DELETE', `/admin/packs/${packId}`),

    /** Every head office this pack is live on, each with its placements and their health. */
    packEnrolments: (packId: string) =>
      send<ReportPackEnrolmentDto[]>('GET', `/admin/packs/${packId}/enrolments`),

    /**
     * Takes a customer off this pack and LEAVES their subscriptions running. The pack stops claiming
     * them; nothing on the report server changes.
     *
     * Two calls rather than one with a `deleteSubscriptions` flag, matching the API: a boolean that
     * flips destructive behaviour is one typo away from stopping a customer's reports.
     */
    forgetPackEnrolment: (packId: string, enrolmentId: string) =>
      send<void>('DELETE', `/admin/packs/${packId}/enrolments/${enrolmentId}`),

    /**
     * Deletes the subscriptions this pack created for one customer, then takes them off the pack.
     * Their reports stop.
     *
     * Answers with the per-subscription outcome rather than nothing: a partial failure is a real
     * result, in which case `enrolmentRemoved` is false and the customer is still on the pack.
     */
    stopPackEnrolment: (packId: string, enrolmentId: string) =>
      send<ReportPackUnenrolResult>('POST', `/admin/packs/${packId}/enrolments/${enrolmentId}/stop`),

    /**
     * What applying this pack to this head office would do. Writes nothing and is safe to re-run,
     * which is what lets the wizard re-ask it every time the customer changes.
     */
    /**
     * Whether one report meets the pack standard, and what is wrong with it if not.
     *
     * Called when a report is picked in the editor, never to render a list: it costs MANDW01 a
     * catalog read and one or two parameter resolves. The same check runs again on save, so this is
     * how the author finds out early rather than how the rule is enforced.
     */
    packReportEligibility: (path: string) =>
      send<ReportPackEligibility>(
        'GET', `/admin/packs/eligibility?path=${encodeURIComponent(path)}`),

    previewPack: (req: PreviewReportPackRequest) =>
      send<ReportPackApplyPreview>('POST', '/admin/packs/preview', req),

    /**
     * Creates the subscriptions. Reports per item and never all-or-nothing — a failure on one is
     * expected to leave the rest created, so the result is a list of outcomes rather than a status.
     */
    applyPack: (req: ApplyReportPackRequest) =>
      send<ReportPackApplyResult>('POST', '/admin/packs/apply', req),

    /**
     * Head offices matching the apply wizard's typeahead. Same Profiles-backed contract as
     * `pickupHeadOfficeSearch`, including `available: false` meaning the directory could not be
     * asked — which must never be rendered as "no such customer".
     */
    packHeadOfficeSearch: (q: string) =>
      send<HeadOfficeSearchResponse>('GET', `/admin/packs/head-offices?q=${encodeURIComponent(q)}`),

    /** Records that an admin has seen a placement's local edit and chosen to keep it. */
    acknowledgePackPlacement: (placementId: string, acknowledged: boolean) =>
      send<void>('POST', `/admin/packs/placements/${placementId}/acknowledge`, { acknowledged }),

    // ── Any Client Detail custom views (admin only, behind the customViews feature flag) ──

    /** Every view + every selectable field on the dynamic report, and where the answer came from. */
    customViewCatalog: () => send<CustomViewCatalog>('GET', '/admin/custom-views/catalog'),

    /** Validates a draft: issues + resolved layout. Pure, so the builder calls it on every edit. */
    customViewValidate: (draft: CustomViewDraft) =>
      send<CustomViewValidationResult>('POST', '/admin/custom-views/validate', draft),

    /**
     * Applies the draft to MANDW01.ReportData via the usp_ReportPortal_SaveDynamicReportView
     * procedure (dev mode: an in-memory sandbox with the same rules). A 400 carries the composer's
     * issue list in `data`.
     */
    createCustomView: (draft: CustomViewDraft) =>
      send<CustomViewApplyResult>('POST', '/admin/custom-views', draft),
    updateCustomView: (viewId: number, draft: CustomViewDraft) =>
      send<CustomViewApplyResult>('PUT', `/admin/custom-views/${viewId}`, draft),

    /** Retire (false) removes the view from the report's dropdown; reactivate (true) restores it. */
    setCustomViewLive: (viewId: number, live: boolean) =>
      send<void>('POST', `/admin/custom-views/${viewId}/${live ? 'enable' : 'disable'}`),

    /** Hard delete. The API refuses while the view is live — retire first. */
    deleteCustomView: (viewId: number) => send<void>('DELETE', `/admin/custom-views/${viewId}`)
  };
}
